import { Component } from 'react';
import { BrowserRouter as Router, Switch, Redirect } from 'react-router-dom';
import momentTz from 'moment-timezone';
import moment from 'moment';
import autobind from 'autobind-decorator';
import RouteItem from './routing/RouteItem';
import Routes from './Routes';
import store from './store';
import { AUTH_CHECK } from './store/actions';
import { I18nextProvider } from 'react-i18next';
import i18n, { fetchTranslations } from '../i18n';
import HttpService from './services/HttpService';
import { removeHTMLElement } from '../ts-helpers/util';
import AddToHomeScreen from '@ideasio/add-to-homescreen-react';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import NoInternetMessage from './views/includes/NoInternetMessage';
import GoogleMapInitiator from './views/includes/GoogleMapInitiator';
import DriverLocation from './views/includes/DriverLocation/DriverLocation';
import Route from './Routes';
import CompanyService from './services/CompanyService';
import AutoLockMessage from './views/includes/AutoLockMessage';
import { connect } from 'react-redux';

type DomainExistsResultType = 'exists' | 'does_not_exists' | 'failed';
type DomainCheckResultType = {
  status: DomainExistsResultType;
  timezone: string;
  company: Nullable<{
    email: string;
    timezone: string;
    name: {
      short: string;
      display: string;
    };
  }>;
};

const mapState = (state) => {
  return {
    user: state.AuthReducer.user,
    isAutoLock: state.AuthReducer.isAutoLock,
  };
};

@(connect(mapState) as any)
class App extends Component<any> {
  state = {
    redirectToLogin: false,

    companyName: '',
    companyExists: true,
    loading: true,
  };

  async componentDidMount() {
    await fetchTranslations();
    this.loadCompanyInfo();
  }

  @autobind
  makeRedirectToLogin() {
    return this.setState(
      {
        redirectToLogin: true,
        loading: false,
      },
      () => {
        this.removeLoader();
      }
    );
  }

  @autobind
  async loadCompanyInfo() {
    const companyName = CompanyService.getShortName();
    if (!companyName) {
      return this.makeRedirectToLogin();
    }

    const {
      status: domainExists,
      timezone: companyTimezone,
      company: companyInformation,
    } = await this.fetchCompanyInfo();

    if (domainExists == 'failed') {
      console.error('Could not check company existence.');
      this.setState({ companyExists: false });
      return this.removeLoader();
    }

    if (domainExists == 'does_not_exists') {
      console.error('Company does not exists.');
      this.setState({ companyExists: false });
      return this.removeLoader();
    }

    if (companyTimezone) {
      momentTz.tz.setDefault(companyTimezone);
      (moment as any).tz.setDefault(companyTimezone);
    }

    await this.authCheck();
    this.removeLoader();

    DriverLocation.initiate();
  }

  @autobind
  private fetchCompanyInfo(): Promise<DomainCheckResultType> {
    let domain = HttpService.getApiHost();

    return new Promise<DomainCheckResultType>((resolve) => {
      if (!domain || 'undefined' == domain) {
        return resolve({
          status: 'does_not_exists',
          timezone: '',
          company: null,
        });
      }

      if (!domain.endsWith('/')) {
        domain += '/';
      }

      domain += 'ping';

      fetch(domain)
        .then((response) => {
          response.json().then((result) => {
            const exists = /pong/g.test(result?.message);
            if (exists) {
              resolve({
                status: 'exists',
                timezone: result.timezone,
                company: result.company,
              });
            }

            resolve({
              status: 'does_not_exists',
              timezone: '',
              company: null,
            });
          });
        })
        .catch((e) => {
          resolve({
            status: 'failed',
            timezone: '',
            company: null,
          });
        });
    });
  }

  @autobind
  removeLoader() {
    this.setState({ loading: false });

    const appLoading: HTMLElement = document.getElementById('app-loading');
    removeHTMLElement(appLoading, 'opacity', 1, 0, 500, () => {
      if (appLoading) {
        appLoading.remove();
      }
    });
  }

  @autobind
  async authCheck() {
    let userState = {
      isLogged: false,
      user: null,
    };

    if (!!localStorage.getItem('access_token')) {
      HttpService.setDefaultHeader('Authorization', `Bearer ${localStorage.getItem('access_token')}`);
      userState = await HttpService.get('/auth/check')
        .then((response) => response.json())
        .catch((e) => {
          if (e instanceof Response) {
            return e.json();
          }

          return e;
        });
    }

    store.dispatch({
      type: AUTH_CHECK,
      payload: userState,
    });
  }

  render() {
    // if ((window?.location?.pathname || '').startsWith('/abcmedtrans')) {
    //   window.location.href = '/';
    //   return null;
    // }

    if (this.props.isAutoLock) {
      window.location.href = Route.toUrl('account_login') + `?email=${this.props.user.email}`;
      return null;
    }

    if (this.state.loading) {
      return null;
    }

    return (
      <GoogleMapInitiator>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <I18nextProvider i18n={i18n}>
            <NoInternetMessage />
            <AutoLockMessage />

            <AddToHomeScreen
              customPromptContent={{
                src: '/images/logo512.png',
              }}
            />

            <Router>
              <Switch>
                {this.state.redirectToLogin && (
                  <Redirect exact={true} from="/" to={Route.toUrl('login_with_email')} />
                )}

                {Routes.redirectList().map((redirect, i) => (
                  <Redirect key={i} {...redirect} />
                ))}

                {Routes.routeList().map((route, i) => (
                  <RouteItem key={i} {...route} />
                ))}
              </Switch>
            </Router>
          </I18nextProvider>
        </MuiPickersUtilsProvider>
      </GoogleMapInitiator>
    );
  }
}

export default App;
